import React, { useState } from 'react';
import DetailsPortal from './DetailsPortal';
import './OrderDetailsPanel.css';
import { useAuth } from '../contexts/AuthContext';
import { sendCorezoidRequest } from '../services/corezoidApi';

const formatJsonPrimitive = (value) => {
  if (value === null) return 'null';
  if (typeof value === 'string') return `"${value}"`;
  return String(value);
};

const JsonTreeNode = ({ name, value, depth = 0 }) => {
  const isArray = Array.isArray(value);
  const isObject = value !== null && typeof value === 'object';

  if (!isObject) {
    return (
      <div className="json-tree-row">
        {name !== undefined && <span className="json-tree-key">{name}:</span>}
        <span className={`json-tree-value json-tree-value-${value === null ? 'null' : typeof value}`}>
          {formatJsonPrimitive(value)}
        </span>
      </div>
    );
  }

  const entries = isArray ? value.map((item, index) => [index, item]) : Object.entries(value);
  const openingBracket = isArray ? '[' : '{';
  const closingBracket = isArray ? ']' : '}';

  return (
    <details className="json-tree-node" open={depth === 0}>
      <summary>
        {name !== undefined && <span className="json-tree-key">{name}:</span>}
        <span className="json-tree-bracket">{openingBracket}</span>
        <span className="json-tree-collapsed-bracket">{closingBracket}</span>
      </summary>
      <div className="json-tree-children">
        {entries.map(([entryName, entryValue]) => (
          <JsonTreeNode
            key={entryName}
            name={isArray ? `[${entryName}]` : entryName}
            value={entryValue}
            depth={depth + 1}
          />
        ))}
      </div>
      <div className="json-tree-closing-bracket">{closingBracket}</div>
    </details>
  );
};

const OrderDetailsPanel = ({ order, pageType, onClose, onStatusChange, onOrderNavigate }) => {
  const { user } = useAuth();
  const [copiedField, setCopiedField] = useState(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusFormData, setStatusFormData] = useState({
    amount: ''
  });
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculateError, setRecalculateError] = useState('');
  const [recalculateSuccess, setRecalculateSuccess] = useState(false);
  const [isCompletingOrder, setIsCompletingOrder] = useState(false);
  const [completeOrderError, setCompleteOrderError] = useState('');
  const [completeOrderSuccess, setCompleteOrderSuccess] = useState(false);
  const [isCompleteConfirmOpen, setIsCompleteConfirmOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancellingOrder, setIsCancellingOrder] = useState(false);
  const [cancelOrderError, setCancelOrderError] = useState('');
  const [openingLinkedOrderId, setOpeningLinkedOrderId] = useState(null);
  const [linkedOrderNavigationError, setLinkedOrderNavigationError] = useState(null);
  const [recalculatedValues, setRecalculatedValues] = useState({
    amountUsdt: null,
    price: null,
    fee: null
  });

  if (!order) return null;

  // Extract data from order.data or use the order itself
  const orderData = order.data || order;

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const formatDateParts = (timestamp) => {
    if (!timestamp) return { date: '-', time: '' };
    try {
      const date = new Date(timestamp * 1000);
      if (isNaN(date.getTime())) {
        const date2 = new Date(timestamp);
        if (isNaN(date2.getTime())) return { date: String(timestamp), time: '' };
        return formatCustomDate(date2);
      }
      return formatCustomDate(date);
    } catch {
      return { date: String(timestamp), time: '' };
    }
  };

  const formatCustomDate = (date) => {
    return {
      date: `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`,
      time: `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`
    };
  };

  const orderId = orderData.OrderId || orderData.orderId || orderData.order_id || '-';
  const requestId = orderData.requestId || orderData.RequestId || orderData.REQUEST_ID || null;
  const clientData = orderData.clientData || orderData.ClientData || orderData.CLIENT_DATA || null;
  const requisiteData = orderData.requisiteData || orderData.RequisiteData || orderData.REQUISITE_DATA || null;
  const rawCorezoidData = order.rawCorezoidData || null;
  const amountFiat = orderData.AmountFiat || orderData.amountFiat || null;
  const fiatCurrency = orderData.FiatCurrency || orderData.fiatCurrency || '';
  const amountUsdt = orderData.AmountUsdt || orderData.amountUsdt || null;
  const price = orderData.Price || orderData.price;
  const fee = orderData.feeUsdt || orderData.FeeUsdt || orderData.fee_usdt;
  const rawOrderData = rawCorezoidData?.data || rawCorezoidData || {};
  const isDuplicate = orderData.double ?? rawOrderData.double;
  const duplicateTo = orderData.duplicateTo ?? rawOrderData.duplicateTo;
  const duplicatedFrom = orderData.duplicatedFrom ?? rawOrderData.duplicatedFrom;
  const shouldShowDuplicateLink = isDuplicate === true
    && duplicateTo !== null
    && duplicateTo !== undefined
    && duplicateTo !== '';
  const shouldShowDuplicatedFromLink = duplicatedFrom !== null
    && duplicatedFrom !== undefined
    && duplicatedFrom !== '';
  const isGatewayUser = user?.role === 'gateway';
  const isAdmin = user?.role === 'admin' || user?.isAdmin;
  const traderName = orderData.traderName || orderData.TraderName || orderData.TRADER_NAME || null;
  const gatewayName = orderData.Gateway || orderData.gateway
    || orderData.gatewayData?.name || orderData.GatewayData?.name || null;
  const createdParts = formatDateParts(
    orderData.created || orderData.Created || orderData.create_time || order.create_time
  );
  const status = (orderData.Status || orderData.status || '').toLowerCase();
  const statusText = orderData.Status || orderData.status || '-';
  const completedTimestamp = orderData.completed ?? orderData.Completed ?? order.completed ?? order.Completed;
  const completedParts = formatDateParts(completedTimestamp);
  const hasAdminView = ['admin', 'support'].includes(user?.role) || user?.isAdmin;
  const canViewExtendedOrderData = ['admin', 'support', 'trader', 'gateway'].includes(user?.role) || user?.isAdmin;
  const isCancelled = status === 'cancelled' || status === 'canceled';
  const isPending = status === 'pending';
  const shouldShowChangeStatus = hasAdminView && pageType === 'payin' && isCancelled;
  const shouldShowCancelOrder = hasAdminView && ['payin', 'payout'].includes(pageType) && isPending;
  const shouldShowClientData = canViewExtendedOrderData && clientData && typeof clientData === 'object';
  const shouldShowRequisiteData = canViewExtendedOrderData && requisiteData && typeof requisiteData === 'object';
  const shouldShowRawData = hasAdminView && rawCorezoidData;
  const visibleClientData = hasAdminView
    ? clientData
    : Object.fromEntries(
      ['amount', 'client_name', 'client_surname']
        .filter(field => Object.prototype.hasOwnProperty.call(clientData || {}, field))
        .map(field => [field, clientData[field]])
    );
  const hasAmountSecondary = amountUsdt !== null && amountUsdt !== undefined && amountUsdt !== '-';
  const hasPrice = price !== null && price !== undefined && price !== '-';

  const openStatusModal = () => {
    setStatusFormData({
      amount: amountFiat ?? ''
    });
    setRecalculatedValues({
      amountUsdt,
      price,
      fee
    });
    setRecalculateError('');
    setRecalculateSuccess(false);
    setCompleteOrderError('');
    setCompleteOrderSuccess(false);
    setIsStatusModalOpen(true);
  };

  const handleStatusFieldChange = (event) => {
    const { name, value } = event.target;
    setStatusFormData((current) => ({ ...current, [name]: value }));
    setRecalculateError('');
    setRecalculateSuccess(false);
    setCompleteOrderError('');
    setCompleteOrderSuccess(false);
  };

  const handleRecalculate = async () => {
    const amount = Number(statusFormData.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      setRecalculateError('Enter an amount greater than zero.');
      setRecalculateSuccess(false);
      return;
    }

    setIsRecalculating(true);
    setRecalculateError('');
    setRecalculateSuccess(false);

    try {
      const response = await sendCorezoidRequest({
        processKey: 'orders',
        data: {
          action: 'recalculate',
          orderId,
          amount
        },
        timeout: 30
      });

      const recalculatedData = response?.data?.ops?.[0]?.data
        || response?.ops?.[0]?.data
        || response?.data?.data
        || null;

      if (!recalculatedData || typeof recalculatedData !== 'object') {
        throw new Error('Corezoid returned no recalculated financial data.');
      }

      const nextAmountUsdt = recalculatedData.amountUsdt
        ?? recalculatedData.AmountUsdt
        ?? recalculatedData.amount_usdt;
      const nextPrice = recalculatedData.price ?? recalculatedData.Price;
      const nextFee = recalculatedData.feeUsdt
        ?? recalculatedData.FeeUsdt
        ?? recalculatedData.fee_usdt
        ?? recalculatedData.fee;

      if (nextAmountUsdt === undefined && nextPrice === undefined && nextFee === undefined) {
        throw new Error('Corezoid response does not contain AmountUsdt, Price, or FeeUsdt.');
      }

      setRecalculatedValues((current) => ({
        amountUsdt: nextAmountUsdt ?? current.amountUsdt,
        price: nextPrice ?? current.price,
        fee: nextFee ?? current.fee
      }));
      setRecalculateSuccess(true);
    } catch (error) {
      setRecalculateError(error.message || 'Failed to recalculate the order.');
    } finally {
      setIsRecalculating(false);
    }
  };

  const openCompleteConfirm = () => {
    const amount = Number(statusFormData.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      setCompleteOrderError('Enter an amount greater than zero.');
      setCompleteOrderSuccess(false);
      return;
    }

    setCompleteOrderError('');
    setIsCompleteConfirmOpen(true);
  };

  const closeCompleteConfirm = () => {
    if (!isCompletingOrder) {
      setIsCompleteConfirmOpen(false);
    }
  };

  const handleCompleteOrder = async () => {
    const amount = Number(statusFormData.amount);

    setIsCompletingOrder(true);
    setCompleteOrderError('');
    setCompleteOrderSuccess(false);

    try {
      await sendCorezoidRequest({
        processKey: 'orders',
        data: {
          action: 'completeOrder',
          orderId,
          amount,
          amountUsdt: recalculatedValues.amountUsdt,
          price: recalculatedValues.price,
          fee: recalculatedValues.fee
        },
        timeout: 30
      });
      setCompleteOrderSuccess(true);
      onStatusChange?.(orderId, 'Completed');
      setIsCompleteConfirmOpen(false);
      setIsStatusModalOpen(false);
    } catch (error) {
      setCompleteOrderError(error.message || 'Failed to complete the order.');
    } finally {
      setIsCompletingOrder(false);
    }
  };

  const openCancelModal = () => {
    setCancelOrderError('');
    setIsCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    if (!isCancellingOrder) {
      setIsCancelModalOpen(false);
    }
  };

  const handleCancelOrder = async () => {
    setIsCancellingOrder(true);
    setCancelOrderError('');

    try {
      await sendCorezoidRequest({
        processKey: 'orders',
        data: {
          action: 'cancelOrder',
          orderId
        },
        timeout: 30
      });
      onStatusChange?.(orderId, 'Cancelled');
      setIsCancelModalOpen(false);
    } catch (error) {
      setCancelOrderError(error.message || 'Failed to cancel the order.');
    } finally {
      setIsCancellingOrder(false);
    }
  };

  const formatAmountPrimary = () => {
    if (amountFiat === null || amountFiat === undefined || amountFiat === '-') return '-';
    return `${amountFiat}${fiatCurrency ? ` ${fiatCurrency}` : ''}`;
  };

  const formatAmountSecondary = () => {
    if (!amountUsdt || amountUsdt === '-') return '-';
    return `${amountUsdt} USDT`;
  };

  const formatPrice = () => {
    if (price === null || price === undefined || price === '-') return '-';
    return `${price}${fiatCurrency ? ` ${fiatCurrency}` : ''}`;
  };

  const formatFee = () => {
    if (fee === null || fee === undefined || fee === '-') return '-';
    return `${fee} USDT`;
  };

  const copyValue = async (value, fieldName) => {
    if (!value || value === '-') return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 1500);
    } catch (err) {
      // Keep panel functional even if clipboard is unavailable.
      console.error(`Failed to copy ${fieldName}:`, err);
    }
  };

  const handleLinkedOrderNavigation = async (targetOrderId) => {
    if (!onOrderNavigate || openingLinkedOrderId !== null) return;

    setOpeningLinkedOrderId(String(targetOrderId));
    setLinkedOrderNavigationError(null);

    try {
      await onOrderNavigate(targetOrderId);
    } catch (error) {
      setLinkedOrderNavigationError({
        orderId: String(targetOrderId),
        message: error.message || 'Failed to open the linked order.'
      });
    } finally {
      setOpeningLinkedOrderId(null);
    }
  };

  const renderLinkedOrder = (label, targetOrderId) => {
    const normalizedTargetId = String(targetOrderId);
    const isOpening = openingLinkedOrderId === normalizedTargetId;
    const navigationError = linkedOrderNavigationError?.orderId === normalizedTargetId
      ? linkedOrderNavigationError.message
      : '';

    return (
      <div className="detail-item duplicate-order-item">
        <span className="detail-label">{label}</span>
        <div className="detail-value">
          <button
            className="duplicate-order-link"
            type="button"
            onClick={() => handleLinkedOrderNavigation(targetOrderId)}
            disabled={openingLinkedOrderId !== null}
            aria-label={`Open linked order ${targetOrderId}`}
          >
            <span className="duplicate-order-link-id">
              {isOpening ? 'Opening…' : formatValue(targetOrderId)}
            </span>
            {!isOpening && (
              <span className="duplicate-order-link-arrow" aria-hidden="true">→</span>
            )}
          </button>
          {navigationError && (
            <div className="duplicate-order-error">{navigationError}</div>
          )}
        </div>
      </div>
    );
  };

  const renderObjectEntries = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return <span className="detail-value">{formatValue(value)}</span>;
    }

    return (
      <div className="nested-detail-list">
        {Object.entries(value).map(([key, nestedValue]) => (
          <div key={key} className="nested-detail-item">
            <span className="nested-detail-key">{key}</span>
            <span className="nested-detail-value">{formatValue(nestedValue)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <DetailsPortal>
    <div className="order-details-overlay" onClick={onClose}>
      <div className="order-details-panel" onClick={(e) => e.stopPropagation()}>
        <div className="order-details-header">
          <h2>Order Details</h2>
          <button type="button" className="close-button" onClick={onClose}>Close</button>
        </div>
        <div className="order-details-content">
          {shouldShowChangeStatus && (
            <button
              type="button"
              className="change-status-button"
              onClick={openStatusModal}
            >
              Change status
            </button>
          )}
          {shouldShowCancelOrder && (
            <button
              type="button"
              className="cancel-order-button"
              onClick={openCancelModal}
            >
              Cancel order
            </button>
          )}
          <div className="order-summary">
            <div className="summary-main">
              <span className="summary-label">Amount</span>
              <span className="summary-primary numeric-value">{formatAmountPrimary()}</span>
              {hasAmountSecondary && <span className="summary-secondary numeric-value">{formatAmountSecondary()}</span>}
            </div>
            <div className="summary-chips">
              <span className={`status-badge status-${status}`}>{statusText}</span>
              {hasPrice && <span className="summary-chip">Price: <span className="numeric-value">{formatPrice()}</span></span>}
              {!isGatewayUser && <span className="summary-chip">Fee: <span className="numeric-value">{formatFee()}</span></span>}
            </div>
          </div>

          <div className="detail-section">
            <h3>Order</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Order ID</span>
                <div className="detail-value with-action">
                  <span className="identifier-value">{formatValue(orderId)}</span>
                  <button
                    className="copy-id-button"
                    onClick={() => copyValue(orderId, 'orderId')}
                    type="button"
                  >
                    {copiedField === 'orderId' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              {requestId && (
                <div className="detail-item">
                  <span className="detail-label">Request ID</span>
                  <div className="detail-value with-action">
                  <span className="identifier-value">{formatValue(requestId)}</span>
                    <button
                      className="copy-id-button"
                      onClick={() => copyValue(requestId, 'requestId')}
                      type="button"
                    >
                      {copiedField === 'requestId' ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-label">Created</span>
                <div className="detail-value">
                  <div className="numeric-value">{createdParts.date}</div>
                  {createdParts.time && <div className="detail-secondary numeric-value">{createdParts.time}</div>}
                </div>
              </div>
              {status === 'completed' && completedTimestamp != null && (
                <div className="detail-item">
                  <span className="detail-label">Completed</span>
                  <div className="detail-value">
                  <div className="numeric-value">{completedParts.date}</div>
                  {completedParts.time && <div className="detail-secondary numeric-value">{completedParts.time}</div>}
                  </div>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-label">Status</span>
                <div className="detail-value">
                  <span className={`status-badge status-${status}`}>{statusText}</span>
                </div>
              </div>
              {shouldShowDuplicatedFromLink && renderLinkedOrder('Duplicated from', duplicatedFrom)}
              {shouldShowDuplicateLink && renderLinkedOrder('Duplicated to', duplicateTo)}
            </div>
          </div>

          {isAdmin && (
            <div className="detail-section">
              <h3>Participants</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Trader</span>
                  <span className="detail-value">{formatValue(traderName)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Gateway</span>
                  <span className="detail-value">{formatValue(gatewayName)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="detail-section">
            <h3>Financial</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Amount</span>
                <div className="detail-value">
                  <div className="numeric-value">{formatAmountPrimary()}</div>
                  {hasAmountSecondary && <div className="detail-secondary numeric-value">{formatAmountSecondary()}</div>}
                </div>
              </div>
              {hasPrice && (
                <div className="detail-item">
                  <span className="detail-label">Price</span>
                <span className="detail-value numeric-value">{formatPrice()}</span>
                </div>
              )}
              {!isGatewayUser && (
                <div className="detail-item">
                  <span className="detail-label">Fee</span>
                <span className="detail-value numeric-value">{formatFee()}</span>
                </div>
              )}
            </div>
          </div>

          {shouldShowClientData && (
            <div className="detail-section">
              <h3>Client Data</h3>
              <div className="detail-item detail-item-nested">
                {renderObjectEntries(visibleClientData)}
              </div>
            </div>
          )}

          {shouldShowRequisiteData && (
            <div className="detail-section">
              <h3>Requisite Data</h3>
              <div className="detail-item detail-item-nested">
                {renderObjectEntries(requisiteData)}
              </div>
            </div>
          )}

          {shouldShowRawData && (
            <details className="detail-section raw-json-section">
              <summary>Raw JSON</summary>
              <div className="json-tree" aria-label="Raw JSON data">
                <JsonTreeNode value={rawCorezoidData} />
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
    {isStatusModalOpen && (
      <div className="change-status-modal-overlay" onClick={() => setIsStatusModalOpen(false)}>
        <div
          className="change-status-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-status-modal-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="change-status-modal-header">
            <div>
              <h2 id="change-status-modal-title">Change status</h2>
              <p>Update the financial details before changing the order status.</p>
            </div>
            <button
              type="button"
              className="change-status-modal-close"
              onClick={() => setIsStatusModalOpen(false)}
              aria-label="Close change status modal"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          <form className="change-status-form" onSubmit={(event) => event.preventDefault()}>
            <div className="change-status-values">
              <div className="change-status-value-item">
                <span className="change-status-value-label">USDT Amount</span>
                <span className="change-status-value-main numeric-value">
                  {recalculatedValues.amountUsdt === null || recalculatedValues.amountUsdt === undefined
                    ? '-'
                    : `${recalculatedValues.amountUsdt} USDT`}
                </span>
              </div>
              <div className="change-status-value-item">
                <span className="change-status-value-label">Price</span>
                <span className="change-status-value-main numeric-value">
                  {recalculatedValues.price === null || recalculatedValues.price === undefined
                    ? '-'
                    : `${recalculatedValues.price}${fiatCurrency ? ` ${fiatCurrency}` : ''}`}
                </span>
              </div>
              <div className="change-status-value-item">
                <span className="change-status-value-label">Fee</span>
                <span className="change-status-value-main numeric-value">
                  {recalculatedValues.fee === null || recalculatedValues.fee === undefined
                    ? '-'
                    : `${recalculatedValues.fee} USDT`}
                </span>
              </div>
            </div>

            <div className="change-status-form-group">
              <label htmlFor="change-status-amount">Amount</label>
              <div className="change-status-amount-row">
                <div className="change-status-input-wrap">
                  <input
                    id="change-status-amount"
                    name="amount"
                    type="number"
                    min="0"
                    step="any"
                    value={statusFormData.amount}
                    onChange={handleStatusFieldChange}
                    placeholder="Enter amount"
                  />
                  {fiatCurrency && <span>{fiatCurrency}</span>}
                </div>
                <button
                  type="button"
                  className="change-status-recalculate-button"
                  onClick={handleRecalculate}
                  disabled={isRecalculating}
                >
                  {isRecalculating ? 'Recalculating...' : 'Recalculate'}
                </button>
              </div>
            </div>

            {recalculateError && (
              <div className="change-status-message change-status-message-error" role="alert">
                {recalculateError}
              </div>
            )}
            {recalculateSuccess && (
              <div className="change-status-message change-status-message-success" role="status">
                Recalculation request completed successfully.
              </div>
            )}

            {completeOrderError && (
              <div className="change-status-message change-status-message-error" role="alert">
                {completeOrderError}
              </div>
            )}
            {completeOrderSuccess && (
              <div className="change-status-message change-status-message-success" role="status">
                Order status changed to completed successfully.
              </div>
            )}

            <button
              type="button"
              className="change-status-complete-button"
              onClick={openCompleteConfirm}
              disabled={isCompletingOrder || isRecalculating}
            >
              Change status to completed
            </button>
          </form>
        </div>
      </div>
    )}
    {isCompleteConfirmOpen && (
      <div className="change-status-modal-overlay" onClick={closeCompleteConfirm}>
        <div
          className="change-status-modal cancel-order-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="complete-order-modal-title"
          aria-describedby="complete-order-modal-description"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="change-status-modal-header complete-order-modal-header">
            <div>
              <h2 id="complete-order-modal-title">Complete order</h2>
              <p id="complete-order-modal-description">
                Are you sure you want to change order {orderId} to completed?
              </p>
            </div>
            <button
              type="button"
              className="change-status-modal-close"
              onClick={closeCompleteConfirm}
              disabled={isCompletingOrder}
              aria-label="Close complete order confirmation"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          <div className="cancel-order-modal-content">
            {completeOrderError && (
              <div className="change-status-message change-status-message-error" role="alert">
                {completeOrderError}
              </div>
            )}
            <div className="cancel-order-modal-actions">
              <button
                type="button"
                className="cancel-order-dismiss-button"
                onClick={closeCompleteConfirm}
                disabled={isCompletingOrder}
              >
                Go back
              </button>
              <button
                type="button"
                className="complete-order-confirm-button"
                onClick={handleCompleteOrder}
                disabled={isCompletingOrder}
              >
                {isCompletingOrder ? 'Changing status...' : 'Yes, complete order'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    {isCancelModalOpen && (
      <div className="change-status-modal-overlay" onClick={closeCancelModal}>
        <div
          className="change-status-modal cancel-order-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-order-modal-title"
          aria-describedby="cancel-order-modal-description"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="change-status-modal-header cancel-order-modal-header">
            <div>
              <h2 id="cancel-order-modal-title">Cancel order</h2>
              <p id="cancel-order-modal-description">
                Are you sure you want to cancel order {orderId}?
              </p>
            </div>
            <button
              type="button"
              className="change-status-modal-close"
              onClick={closeCancelModal}
              disabled={isCancellingOrder}
              aria-label="Close cancel order modal"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          <div className="cancel-order-modal-content">
            {cancelOrderError && (
              <div className="change-status-message change-status-message-error" role="alert">
                {cancelOrderError}
              </div>
            )}
            <div className="cancel-order-modal-actions">
              <button
                type="button"
                className="cancel-order-dismiss-button"
                onClick={closeCancelModal}
                disabled={isCancellingOrder}
              >
                Keep order
              </button>
              <button
                type="button"
                className="cancel-order-confirm-button"
                onClick={handleCancelOrder}
                disabled={isCancellingOrder}
              >
                {isCancellingOrder ? 'Cancelling...' : 'Yes, cancel order'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </DetailsPortal>
  );
};

export default OrderDetailsPanel;
